import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { createSeller, registerByPhone, resendPhoneOtp, verifyPhone } from "../api/auth";
import { ApiError } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { AuthShell } from "../components/AuthShell";
import { Button, Field } from "../components/Page";

/**
 * Self-registration for a partner.
 *
 * Register → verify → sign in, as one flow. The API creates the account as
 * PENDING_VERIFICATION and issues a phone OTP; `login` refuses an unverified
 * contact, so the panel verifies the OTP and then signs the new account in with
 * the password it already has, rather than asking for it a second time.
 *
 * The OTP now reaches the handset: `issueOtp` calls `SmsService.sendOtp` and the
 * MiMSMS gateway is configured. The page used to carry a warning that delivery
 * was unwired; that is stale.
 */
const BD_PHONE = /^\+8801[3-9]\d{8}$/;

export default function RegisterPage() {
  const { signIn } = useAuth();

  const [step, setStep] = useState<"details" | "otp">("details");
  const [partnerName, setPartnerName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Kept only to create the seller profile after the account is verified. Never
  // persisted — same rule as the access token.
  const [pendingProfile, setPendingProfile] = useState<{
    displayName: string;
    businessName?: string;
  } | null>(null);

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  async function onDetailsSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (partnerName.trim().length < 2) {
      setError("Enter your name or the partner's name.");
      return;
    }
    if (businessName.trim().length > 0 && businessName.trim().length < 2) {
      setError("Business name must be at least 2 characters.");
      return;
    }
    if (email.trim() && !EMAIL_RE.test(email.trim())) {
      setError("That email address does not look right.");
      return;
    }
    if (!email.trim()) {
      setError("Enter your email address.");
      return;
    }
    if (!BD_PHONE.test(phone.trim())) {
      setError("Enter a valid Bangladeshi mobile number, e.g. +8801712345678");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("The two passwords do not match.");
      return;
    }

    setBusy(true);
    try {
      await registerByPhone({
        phone: phone.trim(),
        password,
        displayName: partnerName.trim(),
        email: email.trim(),
      });
      setPendingProfile({
        displayName: partnerName.trim(),
        ...(businessName.trim() ? { businessName: businessName.trim() } : {}),
      });
      setStep("otp");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not register. Try again.");
    } finally {
      setBusy(false);
    }
  }

  async function onOtpSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (code.trim().length < 4) {
      setError("Enter the code that was sent to your phone.");
      return;
    }

    setBusy(true);
    try {
      await verifyPhone(phone.trim(), code.trim());
      // The account is active now, so the password already collected signs it in.
      await signIn(phone.trim(), password);
      // Create the seller profile unconditionally, not only when a business name
      // was typed.
      //
      // It used to be conditional, so a partner who left the business name blank
      // — explicitly allowed on this form — reached a dashboard with no profile
      // and met "No seller profile. Create one at POST /sellers." on every page.
      // That message names an HTTP endpoint, which is no use to someone holding
      // a phone. The profile is now created for everyone who finishes signing
      // up, with whatever they gave, and the dashboard just works.
      await createSeller({
        displayName: pendingProfile?.displayName ?? partnerName.trim(),
        ...(pendingProfile?.businessName ? { businessName: pendingProfile.businessName } : {}),
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not verify. Try again.");
    } finally {
      setBusy(false);
    }
  }

  async function onResend() {
    setError(null);
    setBusy(true);
    try {
      await resendPhoneOtp(phone.trim());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not resend. Try again.");
    } finally {
      setBusy(false);
    }
  }

  const onDetails = step === "details";

  return (
    <AuthShell
      wide
      title={onDetails ? "Create your partner account" : "Confirm your phone"}
      lede={
        onDetails
          ? "Register once to list motorcycles and manage showrooms."
          : `We sent a six-digit code to ${phone.trim()}. It is valid for five minutes.`
      }
      footer={
        <p>
          Already have an account?{" "}
          <Link to="/login" className="font-medium text-primary underline">
            Sign in
          </Link>
        </p>
      }
    >
      {/* A genuine two-step sequence, so a progress marker earns its place. */}
      <ol className="mb-5 flex items-center gap-2 text-xs" aria-label="Registration progress">
        <li className="flex items-center gap-1.5">
          <span
            aria-hidden
            className={`flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-semibold ${
              onDetails ? "bg-primary text-white" : "bg-primary-wash text-primary"
            }`}
          >
            1
          </span>
          <span className={onDetails ? "font-medium text-ink" : "text-ink-muted"}>Your details</span>
        </li>
        <li aria-hidden className="h-px flex-1 bg-line" />
        <li className="flex items-center gap-1.5">
          <span
            aria-hidden
            className={`flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-semibold ${
              onDetails ? "bg-sunken text-ink-muted" : "bg-primary text-white"
            }`}
          >
            2
          </span>
          <span className={onDetails ? "text-ink-muted" : "font-medium text-ink"}>Verify phone</span>
        </li>
      </ol>

      <form
        onSubmit={onDetails ? onDetailsSubmit : onOtpSubmit}
        noValidate
        className="space-y-4"
        aria-busy={busy}
      >
        {onDetails ? (
          <>
            <Field
              id="reg-partner"
              name="partnerName"
              label="Your name"
              autoComplete="name"
              required
              value={partnerName}
              onChange={(e) => setPartnerName(e.target.value)}
            />

            <Field
              id="reg-business"
              name="businessName"
              label="Business name"
              autoComplete="organization"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              hint="Optional. Leave blank if you are selling on your own."
            />

            <Field
              id="reg-email"
              name="email"
              type="email"
              label="Email address"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              hint="Required, and this is the only place to set it."
            />

            <Field
              id="reg-phone"
              name="phone"
              type="tel"
              inputMode="tel"
              label="Mobile number"
              autoComplete="tel"
              placeholder="+8801712345678"
              required
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              hint="We send your sign-in code to this number."
            />

            <Field
              id="reg-password"
              name="password"
              type="password"
              label="Password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />

            <Field
              id="reg-confirm"
              name="confirm"
              type="password"
              label="Confirm password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </>
        ) : (
          <div>
            <label className="label" htmlFor="reg-code">
              Verification code
            </label>
            <input
              id="reg-code"
              name="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              required
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              className="field field-mono tnum text-center text-[22px] tracking-[0.3em]"
            />
            <p className="mt-1.5 text-xs text-ink-muted">
              It can take a few seconds to arrive. Check that you entered the number you registered
              with.
            </p>
          </div>
        )}

        {error ? (
          <p role="alert" className="rounded-r border-l-[3px] border-danger bg-danger-wash px-3.5 py-2.5 text-sm text-danger">
            {error}
          </p>
        ) : null}

        <Button type="submit" disabled={busy} className="w-full">
          {busy
            ? onDetails
              ? "Creating your account…"
              : "Verifying…"
            : onDetails
              ? "Create account"
              : "Verify and sign in"}
        </Button>

        {onDetails ? null : (
          <button
            type="button"
            onClick={onResend}
            disabled={busy}
            className="w-full rounded px-3 py-2 text-sm font-medium text-primary underline disabled:opacity-50"
          >
            Send the code again
          </button>
        )}
      </form>
    </AuthShell>
  );
}